import React, { useRef, useState } from 'react';
import { FileText, Upload, Trash2, CheckCircle, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { getStoragePathComponents } from './filenameUtils';
import { sanitizeFilename } from './filenameSanitizer';
import { getFirebaseUploadAuthHeaders } from '../../../utils/firebaseUploadAuth';

const UPLOAD_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/backendDocumentUploaderToFirebase';
const ACCEPTED = '.txt,.md,.json';

const normalizeStoredBaseName = (name) => {
  const stripped = String(name || '')
    .replace(/\.(txt|md|json)$/i, '')
    .trim();

  const safe = sanitizeFilename(stripped, {
    preserveExtension: false,
    allowSpaces: false,
    preserveHyphens: true,
  });

  return safe || 'knowledge-document';
};

const convertToDirectUrl = (url) => {
  if (!url) return '';
  if (!url.includes('firebasestorage.googleapis.com/v0/b/')) return url;

  try {
    const parsed = new URL(url);
    const parts = parsed.pathname.split('/o/');
    if (parts.length === 2) {
      const bucket = parts[0].replace('/v0/b/', '');
      return `https://storage.googleapis.com/${bucket}/${decodeURIComponent(parts[1])}`;
    }
  } catch {
    return url;
  }

  return url;
};

const KnowledgeDocPanel = ({ itinerary, setItinerary }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState('');
  const { currentUser } = useAuth();

  const existingDocs = Array.isArray(itinerary?.metadata?.knowledgeDocs)
    ? itinerary.metadata.knowledgeDocs
    : [];

  const country = itinerary?.metadata?.country;
  const name = itinerary?.metadata?.name;
  const canUpload = !!(country && name);

  const syncKnowledgeMetadata = (docs) => {
    const latest = docs[docs.length - 1] || null;
    setItinerary(prev => ({
      ...prev,
      metadata: {
        ...(prev?.metadata || {}),
        knowledgeDocs: docs,
        knowledgeDocUrl: latest?.url || null,
        knowledgeDocName: latest?.name || null,
        knowledgeDocChars: latest?.chars || null,
        knowledgeDocUploadedAt: latest?.uploadedAt || null
      }
    }));
  };

  const handleFile = async (file) => {
    if (!canUpload) {
      toast.error('Itinerary must have a country and name before uploading.');
      return;
    }

    const text = await file.text();
    setPreview(text.slice(0, 300));

    const wrapped = JSON.stringify({
      type: 'knowledge',
      title: file.name,
      charCount: text.length,
      content: text,
    });

    const blob = new Blob([wrapped], { type: 'application/json' });
    const storedBaseName = normalizeStoredBaseName(file.name);
    const storedFilename = `${storedBaseName}-${Date.now()}.json`;
    const jsonFile = new File([blob], storedFilename, { type: 'application/json' });

    const pathComponents = getStoragePathComponents({ country, name, language: 'en-us' });
    const storagePath = `special_itineraries/${pathComponents.country}/${pathComponents.folderName}/knowledge/${storedFilename}`;

    const formData = new FormData();
    formData.append('document', jsonFile);
    formData.append('storagePath', storagePath);
    formData.append('country', country);
    formData.append('itineraryName', name);

    setUploading(true);
    try {
      const headers = await getFirebaseUploadAuthHeaders(currentUser);
      const res = await fetch(UPLOAD_URL, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      const data = await res.json();
      const directUrl = convertToDirectUrl(data.url || '');

      const nextDoc = {
        name: file.name,
        storedName: storedFilename,
        url: directUrl,
        storagePath,
        chars: text.length,
        uploadedAt: new Date().toISOString()
      };

      syncKnowledgeMetadata([...existingDocs.filter(doc => doc.url !== directUrl), nextDoc]);
      toast.success(`Uploaded "${file.name}" to /knowledge`);
    } catch (err) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleRemove = (index) => {
    const nextDocs = existingDocs.filter((_, currentIndex) => currentIndex !== index);
    syncKnowledgeMetadata(nextDocs);
    toast.success('Knowledge document removed from itinerary metadata');
  };

  return (
    <div className="border border-indigo-200 rounded-lg bg-indigo-50 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-indigo-600" />
        <span className="text-sm font-semibold text-indigo-800">Knowledge Documents</span>
        {existingDocs.length > 0 && <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />}
      </div>

      <p className="text-xs text-indigo-700">
        Upload source documents to <code>/knowledge</code>. These are used to ground the historical context and cultural significance generators.
      </p>

      {existingDocs.length > 0 && (
        <div className="bg-white rounded-md border border-indigo-200 p-3 space-y-2">
          {existingDocs.map((doc, index) => (
            <div key={`${doc.url || doc.name}-${index}`} className="flex items-start justify-between gap-3 border-b border-indigo-100 pb-2 last:border-b-0 last:pb-0">
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-800 truncate">{doc.name}</p>
                <p className="text-[10px] text-gray-400">
                  {doc.chars?.toLocaleString?.() || doc.chars || 0} characters
                </p>
                {doc.url && (
                  <a href={doc.url} target="_blank" rel="noreferrer" className="text-[10px] text-indigo-500 hover:underline truncate block">
                    {doc.url}
                  </a>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="p-1 rounded hover:bg-red-50 text-red-400 shrink-0"
                title="Remove from metadata"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          uploading
            ? 'border-indigo-300 bg-indigo-50 cursor-wait'
            : 'border-indigo-300 hover:border-indigo-500 hover:bg-white'
        }`}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader className="w-5 h-5 text-indigo-500 animate-spin" />
            <p className="text-xs text-indigo-600">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-5 h-5 text-indigo-400" />
            <p className="text-xs text-indigo-700 font-medium">Drop file here or click to browse</p>
            <p className="text-[10px] text-indigo-500">.txt · .md · .json</p>
          </div>
        )}
      </div>

      {preview && (
        <div className="bg-white rounded border border-indigo-200 p-2">
          <p className="text-[10px] text-gray-400 mb-1">Preview</p>
          <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">{preview}...</p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
};

export default KnowledgeDocPanel;
